import { TextField, MenuItem, Autocomplete, CircularProgress, Box } from '@mui/material'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { ReportFilterField } from '../helpers/reports-page-types'

export interface ReportFilterRendererProps {
  filter: ReportFilterField
  value: unknown
  onChange: (name: string, value: unknown) => void
}

export const ReportFilterRenderer = ({ filter, value, onChange }: ReportFilterRendererProps) => {
  const { t } = useTranslation()
  const [asyncOptions, setAsyncOptions] = useState<{ label: string; value: string | number }[]>([])
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    if (filter.fetchOptions) {
      setLoading(true)
      filter
        .fetchOptions()
        .then((opts) => setAsyncOptions(opts))
        .catch(() => setAsyncOptions([]))
        .finally(() => setLoading(false))
    }
  }, [filter.fetchOptions, filter.fetchKey])

  const options = filter.options ?? asyncOptions

  switch (filter.type) {
    case 'select':
      return (
        <TextField
          select
          label={filter.label}
          value={value ?? ''}
          onChange={(e) => onChange(filter.name, e.target.value)}
          size="small"
          fullWidth
          required={filter.required}
          placeholder={filter.placeholder}
          InputProps={{
            endAdornment: loading ? (
              <CircularProgress size={18} sx={{ mr: 2 }} />
            ) : null,
          }}
        >
          <MenuItem value="">
            <em>Selecione</em>
          </MenuItem>
          {options.map((opt) => (
            <MenuItem key={opt.value} value={opt.value}>
              {opt.label}
            </MenuItem>
          ))}
        </TextField>
      )

    case 'autocomplete':
      return (
        <Autocomplete
          options={options}
          getOptionLabel={(opt) =>
            typeof opt === 'string' ? opt : opt.label
          }
          loading={loading}
          value={
            options.find(
              (o) => o.value === value
            ) ?? null
          }
          onChange={(_, newVal) =>
            onChange(filter.name, newVal ? newVal.value : null)
          }
          size="small"
          fullWidth
          renderInput={(params) => (
            <TextField
              {...params}
              label={filter.label}
              required={filter.required}
              placeholder={filter.placeholder}
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {loading ? <CircularProgress size={18} /> : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
        />
      )

    case 'number':
      return (
        <TextField
          type="number"
          label={filter.label}
          value={value ?? ''}
          onChange={(e) => onChange(filter.name, Number(e.target.value))}
          size="small"
          fullWidth
          required={filter.required}
          placeholder={filter.placeholder}
        />
      )

    case 'pagination':
      return (
        <Box sx={{ display: 'flex', gap: 1 }}>
          <TextField
            type="number"
            label={t('reports.page.filter.renderer.page')}
            value={(value as { page?: number })?.page ?? 1}
            onChange={(e) =>
              onChange(filter.name, {
                ...(value as object),
                page: Number(e.target.value),
              })
            }
            size="small"
            inputProps={{ min: 1 }}
            sx={{ width: 120 }}
          />
          <TextField
            type="number"
            label={t('reports.page.filter.renderer.per.page')}
            value={(value as { perPage?: number })?.perPage ?? 50}
            onChange={(e) =>
              onChange(filter.name, {
                ...(value as object),
                perPage: Number(e.target.value),
              })
            }
            size="small"
            inputProps={{ min: 1, max: 500 }}
            sx={{ width: 120 }}
          />
        </Box>
      )

    case 'text':
    default:
      return (
        <TextField
          label={filter.label}
          value={value ?? ''}
          onChange={(e) => onChange(filter.name, e.target.value)}
          size="small"
          fullWidth
          required={filter.required}
          placeholder={filter.placeholder}
        />
      )
  }
}
